import { getChatCompletion } from './aiIntegrations/chatCompletion';

const MODEL = 'gpt-4o';
const PROVIDER = 'OPEN_AI';

/**
 * AI Compliance Service
 * Uses rule-based data as input - AI provides insights, patterns, and summaries only.
 * Rule-based logic remains the source of truth for hard compliance checks.
 */
export const aiComplianceService = {
  /**
   * Analyse a single site's compliance data and return AI-generated insights.
   */
  async analyseSite(siteData) {
    const {
      siteName,
      score,
      riskLevel,
      alerts = [],
      incidents = [],
      snags = [],
      tasks = [],
      activities = [],
      documents = [],
    } = siteData;

    const systemPrompt = `You are a construction compliance analyst. You receive structured compliance data for a construction site and provide concise, actionable insights for site managers and admin users.

Rules:
- Be specific and practical - avoid generic advice
- Focus on patterns, risks, and priority actions
- Keep each insight to 1-2 sentences
- Return valid JSON only, no markdown, no explanation outside the JSON`;

    const alertLines = alerts?.slice(0, 15)?.map(a => `- [${a?.severity?.toUpperCase()}] ${a?.type}: ${a?.description}`)?.join('\n') || 'None';
    const incidentLines = incidents?.slice(0, 10)?.map(i => `- ${i?.incident_type || 'Incident'} | Severity: ${i?.severity_level || 'unknown'} | ${i?.description ? i?.description?.slice(0, 80) : ''}`)?.join('\n') || 'None';
    const openSnags = snags?.filter(s => s?.status !== 'resolved');
    const snagLines = openSnags?.slice(0, 10)?.map(s => `- [${s?.priority}] ${s?.title || 'Untitled snag'}: ${s?.description ? s?.description?.slice(0, 60) : ''}`)?.join('\n') || 'None';
    const taskLines = tasks?.slice(0, 10)?.map(t => `- ${t?.title || t?.category || 'Task'} | Status: ${t?.status} | Required: ${t?.required}`)?.join('\n') || 'None';
    const activityLines = activities?.slice(0, 15)?.map(a => `- ${a?.activity_type} | ${a?.severity_level ? 'Severity: ' + a?.severity_level + ' | ' : ''}${a?.title}`)?.join('\n') || 'None';
    const docLines = documents?.filter(d => d?.expiry_date)?.slice(0, 8)?.map(d => `- ${d?.title} (${d?.category}) expires: ${d?.expiry_date}`)?.join('\n') || 'None';

    const userPrompt = `Analyse this construction site compliance data and return insights.

Site: ${siteName}
Compliance Score: ${score}/100
Risk Level: ${riskLevel}

Rule-Based Alerts (${alerts?.length} total):
${alertLines}

Recent Incidents (last 30 days, ${incidents?.length} total):
${incidentLines}

Open Snags (${openSnags?.length} open):
${snagLines}

Recent Tasks (last 30 days):
${taskLines}

Recent Activities (last 30 days, ${activities?.length} total):
${activityLines}

Expiring Documents:
${docLines}

Return a JSON object with exactly these keys:
{
  "insights": ["2-4 plain-English observations about this site\'s compliance health"],
  "patterns": ["1-3 patterns detected, e.g. repeated incident types, recurring missed tasks"],
  "priorityActions": ["2-4 specific actions the site manager should take this week"],
  "riskFactors": ["1-3 risk factors that could worsen compliance if not addressed"]
}`;

    try {
      const response = await getChatCompletion(
        PROVIDER,
        MODEL,
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        {
          max_completion_tokens: 800,
          response_format: { type: 'json_object' },
        }
      );

      const content = response?.choices?.[0]?.message?.content || '{}';
      const parsed = JSON.parse(content);

      return {
        insights: parsed?.insights || [],
        patterns: parsed?.patterns || [],
        priorityActions: parsed?.priorityActions || [],
        riskFactors: parsed?.riskFactors || [],
      };
    } catch (err) {
      console.error('[aiComplianceService] analyseSite error:', err);
      return {
        insights: ['Unable to generate AI insights at this time.'],
        patterns: [],
        priorityActions: [],
        riskFactors: [],
      };
    }
  },

  /**
   * Generate a plain-English weekly compliance summary for head office covering all sites.
   */
  async generateWeeklySummary(allSitesData) {
    const systemPrompt = `You are a construction compliance reporting assistant for a head office team. Write clear, professional weekly compliance summaries that highlight what needs attention across all sites. Be concise and direct.`;

    const siteSummaries = (allSitesData || [])?.map(site => {
      const criticalAlerts = (site?.alerts || [])?.filter(a => a?.severity === 'critical')?.length;
      const warningAlerts = (site?.alerts || [])?.filter(a => a?.severity === 'warning')?.length;
      const openSnags = (site?.snags || [])?.filter(s => s?.status !== 'resolved')?.length;
      const recentIncidents = (site?.incidents || [])?.length;
      return `${site?.siteName}: Score ${site?.score}/100 (${site?.riskLevel} risk) - ${criticalAlerts} critical alerts, ${warningAlerts} warnings, ${openSnags} open snags, ${recentIncidents} incidents (30d)`;
    })?.join('\n');

    const totalCritical = (allSitesData || [])?.reduce((sum, s) => sum + (s?.alerts || [])?.filter(a => a?.severity === 'critical')?.length, 0);
    const highRiskSites = (allSitesData || [])?.filter(s => s?.riskLevel === 'High')?.map(s => s?.siteName)?.join(', ');
    const avgScore = allSitesData?.length > 0
      ? Math.round((allSitesData || [])?.reduce((sum, s) => sum + (s?.score || 0), 0) / allSitesData?.length)
      : 0;

    const userPrompt = `Write a weekly compliance summary for head office covering ${allSitesData?.length} construction sites.

Overall: Average score ${avgScore}/100, ${totalCritical} critical alerts across all sites.
High risk sites: ${highRiskSites || 'None'}

Site breakdown:
${siteSummaries}

Write 3-5 paragraphs covering:
1. Overall compliance health this week
2. Sites requiring immediate attention and why
3. Key patterns or recurring issues across sites
4. Recommended actions for head office this week

Keep it professional, factual, and under 300 words.`;

    try {
      const response = await getChatCompletion(
        PROVIDER,
        MODEL,
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt },
        ],
        { max_completion_tokens: 600 }
      );

      return response?.choices?.[0]?.message?.content || 'Unable to generate weekly summary at this time.';
    } catch (err) {
      console.error('[aiComplianceService] generateWeeklySummary error:', err);
      return 'Unable to generate weekly summary at this time. Please try again.';
    }
  },
};
